import React, { useState } from "react";
import { storage } from "../lib/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { updatePackageCustomerDetails } from "../lib/packages";
import { Modal, toast } from "./UI";

export default function PackageDetailsModal({ pkg, onClose, onSaved }) {
  const [itemName,   setItemName]   = useState(pkg.itemName   || "");
  const [itemValue,  setItemValue]  = useState(pkg.itemValue  || "");
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [preview,    setPreview]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState("");

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError("File must be under 10MB."); return; }
    setInvoiceFile(file);
    // Preview for images
    if (file.type.startsWith("image/")) {
      const r = new FileReader();
      r.onload = e => setPreview(e.target.result);
      r.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const save = async () => {
    if (!itemName.trim()) { setError("Item name is required."); return; }
    if (!itemValue)       { setError("Item value is required."); return; }
    setSaving(true); setError("");
    try {
      let invoiceURL  = pkg.invoiceURL  || null;
      let invoicePath = pkg.invoicePath || null;

      // Upload invoice file if provided
      if (invoiceFile) {
        invoicePath = `invoices/${pkg.id}_${Date.now()}_${invoiceFile.name}`;
        const storageRef = ref(storage, invoicePath);
        await uploadBytes(storageRef, invoiceFile);
        invoiceURL = await getDownloadURL(storageRef);
      }

      await updatePackageCustomerDetails(pkg.id, {
        itemName:    itemName.trim(),
        itemValue:   itemValue.toString().trim(),
        invoiceURL,
        invoicePath,
      });

      toast("Package details saved!");
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Package details" onClose={onClose}>
      <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>
        Enter the item details for <strong>{pkg.description || pkg.id}</strong>. This is required for customs and shipping purposes.
      </p>

      {error && <div className="error-msg">{error}</div>}

      <div className="form-row">
        <label>Item name *</label>
        <input
          value={itemName}
          onChange={e => setItemName(e.target.value)}
          placeholder="e.g. Nike Shoes, iPhone Case, Books..."
        />
      </div>

      <div className="form-row">
        <label>Item value (USD) *</label>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", fontSize: 14 }}>$</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={itemValue}
            onChange={e => setItemValue(e.target.value)}
            placeholder="0.00"
            style={{ paddingLeft: 26 }}
          />
        </div>
      </div>

      <div className="form-row">
        <label>Upload invoice / receipt (optional)</label>
        <div style={{
          border: "2px dashed var(--border-md)",
          borderRadius: "var(--radius)",
          padding: "20px",
          textAlign: "center",
          cursor: "pointer",
          background: "var(--bg-soft)",
        }}
          onClick={() => document.getElementById("invoice-upload").click()}
        >
          {preview ? (
            <img src={preview} alt="Invoice preview" style={{ maxHeight: 160, maxWidth: "100%", borderRadius: 8 }} />
          ) : invoiceFile ? (
            <div>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{invoiceFile.name}</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{(invoiceFile.size / 1024).toFixed(0)} KB</div>
            </div>
          ) : pkg.invoiceURL ? (
            <div>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--green-dark)" }}>Invoice already uploaded</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>Click to replace</div>
              <a href={pkg.invoiceURL} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 12, color: "var(--blue)", marginTop: 4, display: "block" }}>View current invoice ↗</a>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📎</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Click to upload invoice</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>PDF, JPG, PNG — max 10MB</div>
            </div>
          )}
          <input
            id="invoice-upload"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            style={{ display: "none" }}
            onChange={handleFile}
          />
        </div>
      </div>

      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? "Saving..." : "Save details"}
        </button>
      </div>
    </Modal>
  );
}
